<template>
  <template v-if="versionLoading">
    <span class="loader is-size-2 mx-auto"></span>
  </template>
  <div
    class="card is-clickable"
    v-else-if="version"
  >
    <router-link
      :to="{ name: 'worker-version', params: { versionId: version.id } }"
      target="_blank"
      title="View details for this worker version"
    >
      <div class="card-content p-3 has-text-dark">
        <span
          v-if="version.state"
          class="tag is-capitalized is-pulled-right mt-1"
          :class="WORKER_VERSION_STATE_COLORS[version.state]"
        >
          {{ version.state }}
        </span>

        <div
          v-if="version.tag"
          class="version-attribute"
        >
          <i
            class="icon-tags"
            title="Tag"
          ></i>
          {{ version.tag }}
        </div>
        <div
          v-if="version.branch"
          class="version-attribute"
        >
          <i
            class="icon-branch"
            title="Branch"
          ></i
          >{{ version.branch }}
        </div>
        <div class="version-attribute">
          created <span :title="version.created">{{ relativeCreated }}</span>
        </div>
      </div>
    </router-link>
    <div
      class="card-footer"
      v-if="selectFunction"
    >
      <!-- Allows to select a worker version in the process configuration view -->
      <a
        class="card-footer-item button is-success"
        :class="{ 'is-loading': addLoading }"
        v-on:click="select"
        title="Add the recommended worker version to the process"
      >
        <i class="icon-plus"></i> Add to process
      </a>
    </div>
  </div>
  <div
    class="notification is-danger"
    v-else-if="versionError"
  >
    Error while loading the recommended worker version:<br />
    {{ versionError }}
  </div>
  <div
    class="notification is-warning"
    v-else
  >
    No recommended worker version
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { WORKER_VERSION_STATE_COLORS } from '@/config'
import { ago, errorParser } from '@/helpers'
import { useNotificationStore, useWorkerStore } from '@/stores'
import type { UUID } from '@/types'
import type { WorkerVersion } from '@/types/worker'

const { workerId, selectFunction } = defineProps<{
  workerId: UUID
  /**
   * A function called when the recommended WorkerVersion is selected.
   * The button to select a worker version is only shown when this function is set.
   */
  selectFunction?: (version: WorkerVersion) => unknown | Promise<unknown>
}>()

const versionLoading = ref(false)
const versionError = ref<string | null>(null)
const addLoading = ref(false)

const notificationStore = useNotificationStore()
const workerStore = useWorkerStore()

const version = computed<WorkerVersion | null>(
  () => workerStore.recommendedWorkerVersions[workerId] ?? null,
)

const relativeCreated = computed<string>(() => {
  if (!version.value) return ''
  const output = ago(new Date(version.value.created))
  // Add a "on" prefix for absolute dates, so that the date reads `Created on 2042-02-31`
  if (output.endsWith('ago')) return output
  return `on ${output}`
})

async function fetchRecommendedVersion() {
  versionLoading.value = true
  try {
    await workerStore.getRecommendedWorkerVersion(workerId)
  } catch (err) {
    versionError.value = errorParser(err)
  } finally {
    versionLoading.value = false
  }
}

async function select() {
  if (addLoading.value || !selectFunction || !version.value) return
  addLoading.value = true
  try {
    await selectFunction(version.value)
  } catch (err) {
    notificationStore.notify({ type: 'error', text: errorParser(err) })
  } finally {
    addLoading.value = false
  }
}

watch(
  () => workerId,
  (newValue: UUID) => {
    if (!(newValue in workerStore.recommendedWorkerVersions)) fetchRecommendedVersion()
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.card {
  max-width: 20rem;
}

// Merge the border of buttons used in the card footer with the card itself, so that colored borders override the card's
.card-footer-item.button {
  margin: -1px;
  margin-bottom: -var('--bulma-thickness');
}

.version-attribute {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 15ch;
}

.notification {
  padding: 1rem;
}
</style>
